import { api } from '@/lib/api/client';

// Tier-3 (Faxter-Clerk only) operations console. Talks to /api/clerk-ops,
// which 404s for any tenant without the CLERK_OPS_CONSOLE feature. Response
// shapes mirror docs/clerk-ops-api-contract.md; the backend returns snake_case
// (Pydantic model_dump), mapped to camelCase here per the analytics pattern.

// The activation funnel Clerk manages against: signup -> bank statement (24h)
// -> P&L back (48h) -> subscription (7d). Internal targets, not customer
// deadlines — the trial runs its full length regardless, so an overdue
// milestone means we should act, not that the customer is locked out.
export type JourneyMilestoneState = 'done' | 'ready' | 'due' | 'overdue' | 'blocked';

export interface JourneyMilestone {
  key: 'statement' | 'pnl' | 'subscription';
  state: JourneyMilestoneState;
  at: string | null;
  dueAt: string | null;
  detail: string;
}

export interface JourneyRow {
  clientId: string;
  name: string;
  milestones: JourneyMilestone[];
  /** Evidence about the books, not a verdict about the customer. */
  issues: string[];
  daysSinceSignup: number;
  trialDaysLeft: number | null;
  lastCustomerActivityAt: string | null;
  stalled: boolean;
}

export interface Journey {
  rows: JourneyRow[];
  total: number;
  stalled: number;
  awaitingPnl: number;
}

export interface LastAction {
  type: string | null;
  label: string | null;
  at: string | null;
}

export interface OpsClient {
  clientId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  onboardingStage: string | null;
  ownerName: string | null;
  health: string | null;
  createdAt: string | null;
  wentLiveAt: string | null;
  lastActiveAt: string | null;
  lastAction: LastAction | null;
  featureProgress: { used: number; total: number } | null;
}

export interface Money {
  amount: string;
  currency: string | null;
}

export interface BillingSummary {
  currency: string | null;
  mrr: Money | null;
  revenueThisMonth: Money | null;
  revenuePrevMonth: Money | null;
  outstandingTotal: Money | null;
  activeSubscriptions: number;
  failedPaymentsCount: number;
  refundsThisMonth: { count: number; amount: Money | null } | null;
}

export interface Payment {
  paymentId: string;
  clientId: string | null;
  amount: Money | null;
  status: string | null;
  method: string | null;
  description: string | null;
  createdAt: string | null;
}

export interface AtRiskAccount {
  clientId: string;
  reason: string | null;
  amountOverdue: Money | null;
  daysOverdue: number;
  failedAttempts: number;
}

export interface Invoice {
  invoiceId: string;
  amount: Money | null;
  status: string | null;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
}

export interface ClientBilling {
  plan: string | null;
  subscriptionStatus: string | null;
  balance: Money | null;
  nextInvoiceAt: string | null;
  invoices: Invoice[];
}

export interface ClerkCustomerSummary {
  linked: boolean;
  reason: string | null;
  client: OpsClient | null;
  billing: ClientBilling | null;
}

export interface FeatureUsage {
  key: string;
  label: string | null;
  status: string | null;
  firstUsedAt: string | null;
  lastUsedAt: string | null;
  count: number;
}

export interface ClientActivity {
  clientId: string;
  features: FeatureUsage[];
  recentActions: LastAction[];
}

export interface OnboardingMetrics {
  inOnboarding: number;
  completionRate30d: number | null;
  avgTimeToCompleteDays: number | null;
  avgTimeToFirstValueDays: number | null;
  stalled: { count: number; clientIds: string[] };
  upcomingCalls: { clientId: string; scheduledAt: string; ownerId: string | null }[];
}

export interface OperationsMetrics {
  payrollRunsCompleted: number;
  booksClosedThisMonth: number;
  booksTotalThisMonth: number;
  filingSuccessRate: number | null;
  documentsAwaitingCustomer: number;
  complianceDeadlinesDue7d: number;
}

export interface OutstandingDocument {
  clientId: string;
  documentType: string | null;
  label: string;
  status: string | null;
  requestedAt: string | null;
  dueAt: string | null;
}

export interface ComplianceDeadline {
  clientId: string;
  deadlineType: string | null;
  label: string;
  dueAt: string;
  status: string | null;
}

export interface Lead {
  id: number;
  name: string;
  email: string;
  businessName: string | null;
  phone: string | null;
  companySize: string | null;
  message: string | null;
  source: string | null;
  createdAt: string | null;
}

export interface SubscriptionPayment {
  id: number;
  tenantId: number;
  tenantName: string | null;
  reference: string;
  cycle: string | null;
  tiers: string[];
  amountNgn: string;
  status: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
}

function mapClient(raw: Record<string, any>): OpsClient {
  const la = raw.last_action;
  const fp = raw.feature_progress;
  return {
    clientId: raw.client_id,
    name: raw.name ?? null,
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    status: raw.status ?? null,
    onboardingStage: raw.onboarding_stage ?? null,
    ownerName: raw.owner?.name ?? null,
    health: raw.health ?? null,
    createdAt: raw.created_at ?? null,
    wentLiveAt: raw.went_live_at ?? null,
    lastActiveAt: raw.last_active_at ?? null,
    lastAction: la ? { type: la.type ?? null, label: la.label ?? null, at: la.at ?? null } : null,
    featureProgress: fp ? { used: fp.used ?? 0, total: fp.total ?? 0 } : null,
  };
}

function mapMoney(raw: any): Money | null {
  return raw ? { amount: raw.amount ?? '0', currency: raw.currency ?? null } : null;
}

function mapBillingSummary(raw: Record<string, any>): BillingSummary {
  return {
    currency: raw.currency ?? null,
    mrr: mapMoney(raw.mrr),
    revenueThisMonth: mapMoney(raw.revenue_this_month),
    revenuePrevMonth: mapMoney(raw.revenue_prev_month),
    outstandingTotal: mapMoney(raw.outstanding_total),
    activeSubscriptions: raw.active_subscriptions ?? 0,
    failedPaymentsCount: raw.failed_payments_count ?? 0,
    refundsThisMonth: raw.refunds_this_month
      ? { count: raw.refunds_this_month.count ?? 0, amount: mapMoney(raw.refunds_this_month.amount) }
      : null,
  };
}

function mapPayment(raw: Record<string, any>): Payment {
  return {
    paymentId: raw.payment_id,
    clientId: raw.client_id ?? null,
    amount: mapMoney(raw.amount),
    status: raw.status ?? null,
    method: raw.method ?? null,
    description: raw.description ?? null,
    createdAt: raw.created_at ?? null,
  };
}

function mapAtRisk(raw: Record<string, any>): AtRiskAccount {
  return {
    clientId: raw.client_id,
    reason: raw.reason ?? null,
    amountOverdue: mapMoney(raw.amount_overdue),
    daysOverdue: raw.days_overdue ?? 0,
    failedAttempts: raw.failed_attempts ?? 0,
  };
}

function mapInvoice(raw: Record<string, any>): Invoice {
  return {
    invoiceId: raw.invoice_id,
    amount: mapMoney(raw.amount),
    status: raw.status ?? null,
    issuedAt: raw.issued_at ?? null,
    dueAt: raw.due_at ?? null,
    paidAt: raw.paid_at ?? null,
  };
}

function mapClientBilling(raw: Record<string, any>): ClientBilling {
  return {
    plan: raw.plan ?? null,
    subscriptionStatus: raw.subscription_status ?? null,
    balance: mapMoney(raw.balance),
    nextInvoiceAt: raw.next_invoice_at ?? null,
    invoices: (raw.invoices ?? []).map(mapInvoice),
  };
}

function mapOnboarding(raw: Record<string, any>): OnboardingMetrics {
  const stalled = raw.stalled || {};
  return {
    inOnboarding: raw.in_onboarding ?? 0,
    completionRate30d: raw.completion_rate_30d ?? null,
    avgTimeToCompleteDays: raw.avg_time_to_complete_days ?? null,
    avgTimeToFirstValueDays: raw.avg_time_to_first_value_days ?? null,
    stalled: { count: stalled.count ?? 0, clientIds: stalled.client_ids ?? [] },
    upcomingCalls: (raw.upcoming_calls ?? []).map((c: Record<string, any>) => ({
      clientId: c.client_id,
      scheduledAt: c.scheduled_at,
      ownerId: c.owner_id ?? null,
    })),
  };
}

function mapOperations(raw: Record<string, any>): OperationsMetrics {
  return {
    payrollRunsCompleted: raw.payroll_runs_completed ?? 0,
    booksClosedThisMonth: raw.books_closed_this_month ?? 0,
    booksTotalThisMonth: raw.books_total_this_month ?? 0,
    filingSuccessRate: raw.filing_success_rate ?? null,
    documentsAwaitingCustomer: raw.documents_awaiting_customer ?? 0,
    complianceDeadlinesDue7d: raw.compliance_deadlines_due_7d ?? 0,
  };
}

function mapDocument(raw: Record<string, any>): OutstandingDocument {
  return {
    clientId: raw.client_id,
    documentType: raw.document_type ?? null,
    label: raw.label,
    status: raw.status ?? null,
    requestedAt: raw.requested_at ?? null,
    dueAt: raw.due_at ?? null,
  };
}

function mapAction(raw: Record<string, any>): LastAction {
  return { type: raw.type ?? null, label: raw.label ?? null, at: raw.at ?? null };
}

function mapFeatureUsage(raw: Record<string, any>): FeatureUsage {
  return {
    key: raw.key,
    label: raw.label ?? null,
    status: raw.status ?? null,
    firstUsedAt: raw.first_used_at ?? null,
    lastUsedAt: raw.last_used_at ?? null,
    count: raw.count ?? 0,
  };
}

function mapActivity(raw: Record<string, any>): ClientActivity {
  return {
    clientId: raw.client_id,
    features: (raw.features ?? []).map(mapFeatureUsage),
    recentActions: (raw.recent_actions ?? []).map(mapAction),
  };
}

function mapDeadline(raw: Record<string, any>): ComplianceDeadline {
  return {
    clientId: raw.client_id,
    deadlineType: raw.deadline_type ?? null,
    label: raw.label,
    dueAt: raw.due_at,
    status: raw.status ?? null,
  };
}

function mapLead(raw: Record<string, any>): Lead {
  return {
    id: raw.id,
    name: raw.name,
    email: raw.email,
    businessName: raw.business_name ?? null,
    phone: raw.phone ?? null,
    companySize: raw.company_size ?? null,
    message: raw.message ?? null,
    source: raw.source ?? null,
    createdAt: raw.created_at ?? null,
  };
}

function mapSubscriptionPayment(raw: Record<string, any>): SubscriptionPayment {
  return {
    id: raw.id,
    tenantId: raw.tenant_id,
    tenantName: raw.tenant_name ?? null,
    reference: raw.reference,
    cycle: raw.cycle ?? null,
    tiers: (raw.line_items ?? []).map((li: Record<string, any>) => li.tier),
    amountNgn: String(raw.amount_ngn ?? '0'),
    status: raw.status ?? null,
    submittedAt: raw.submitted_at ?? null,
    verifiedAt: raw.verified_at ?? null,
  };
}

function mapJourneyRow(raw: Record<string, any>): JourneyRow {
  return {
    clientId: raw.client_id ?? '',
    name: raw.name ?? '',
    milestones: (raw.milestones ?? []).map((m: Record<string, any>) => ({
      key: m.key,
      state: m.state,
      at: m.at ?? null,
      dueAt: m.due_at ?? null,
      detail: m.detail ?? '',
    })),
    issues: raw.issues ?? [],
    daysSinceSignup: raw.days_since_signup ?? 0,
    trialDaysLeft: raw.trial_days_left ?? null,
    lastCustomerActivityAt: raw.last_customer_activity_at ?? null,
    stalled: Boolean(raw.stalled),
  };
}

export const clerkOpsService = {
  async isEnabled(): Promise<boolean> {
    // 404 (feature off) is a silent error in the api client; treat any failure
    // as "not enabled" so the nav item stays hidden.
    try {
      await api.get<{ enabled: boolean }>('/clerk-ops/enabled');
      return true;
    } catch {
      return false;
    }
  },

  async getClients(): Promise<OpsClient[]> {
    const page = await api.get<{ data: Record<string, any>[] }>('/clerk-ops/clients?limit=200');
    return (page.data ?? []).map(mapClient);
  },

  async getOnboardingMetrics(): Promise<OnboardingMetrics> {
    return mapOnboarding(await api.get<Record<string, any>>('/clerk-ops/metrics/onboarding'));
  },

  async getOperationsMetrics(period = 'this_month'): Promise<OperationsMetrics> {
    return mapOperations(
      await api.get<Record<string, any>>(`/clerk-ops/metrics/operations?period=${period}`)
    );
  },

  async getSubscriptionPayments(status = 'pending'): Promise<SubscriptionPayment[]> {
    const rows = await api.get<Record<string, any>[]>(`/clerk-ops/subscription-payments?status=${status}`);
    return (rows ?? []).map(mapSubscriptionPayment);
  },

  async getLeads(days = 30, limit = 100): Promise<Lead[]> {
    const res = await api.get<{ leads: Record<string, any>[] }>(
      `/clerk-ops/leads?days=${days}&limit=${limit}`
    );
    return (res.leads ?? []).map(mapLead);
  },

  async getOutstandingDocuments(): Promise<OutstandingDocument[]> {
    const page = await api.get<{ data: Record<string, any>[] }>('/clerk-ops/documents/outstanding');
    return (page.data ?? []).map(mapDocument);
  },

  async getComplianceDeadlines(withinDays = 30): Promise<ComplianceDeadline[]> {
    const page = await api.get<{ data: Record<string, any>[] }>(
      `/clerk-ops/compliance/deadlines?within_days=${withinDays}`
    );
    return (page.data ?? []).map(mapDeadline);
  },

  async getBillingSummary(): Promise<BillingSummary> {
    return mapBillingSummary(await api.get<Record<string, any>>('/clerk-ops/billing/summary'));
  },

  async getPayments(): Promise<Payment[]> {
    const page = await api.get<{ data: Record<string, any>[] }>('/clerk-ops/billing/payments?limit=50');
    return (page.data ?? []).map(mapPayment);
  },

  async getAtRisk(): Promise<AtRiskAccount[]> {
    const page = await api.get<{ data: Record<string, any>[] }>('/clerk-ops/billing/at-risk');
    return (page.data ?? []).map(mapAtRisk);
  },

  async getClientBilling(clientId: string): Promise<ClientBilling> {
    return mapClientBilling(
      await api.get<Record<string, any>>(`/clerk-ops/billing/clients/${clientId}`)
    );
  },

  async getClientActivity(clientId: string): Promise<ClientActivity> {
    return mapActivity(
      await api.get<Record<string, any>>(`/clerk-ops/clients/${clientId}/activity`)
    );
  },

  async getCustomerBusiness(customerId: string): Promise<ClerkCustomerSummary> {
    const raw = await api.get<Record<string, any>>(`/clerk-ops/customers/${customerId}/summary`);
    return {
      linked: !!raw.linked,
      reason: raw.reason ?? null,
      client: raw.client ? mapClient(raw.client) : null,
      billing: raw.billing ? mapClientBilling(raw.billing) : null,
    };
  },

  async getJourney(stalledOnly = false): Promise<Journey> {
    const res = await api.get<Record<string, any>>(
      `/clerk-ops/journey${stalledOnly ? '?stalled_only=true' : ''}`
    );
    return {
      rows: (res.data ?? []).map(mapJourneyRow),
      total: res.total ?? 0,
      stalled: res.stalled ?? 0,
      awaitingPnl: res.awaiting_pnl ?? 0,
    };
  },
};

export function formatMoney(m: Money | null): string {
  if (!m) return '—';
  const n = Number(m.amount);
  const num = Number.isNaN(n) ? m.amount : n.toLocaleString();
  return m.currency ? `${m.currency} ${num}` : `${num}`;
}
